/**
 * DocumentSlotsSection — the entity-dialog document area, rebuilt as the
 * universal tile grid (slot-1 T3 / brief §2.1).
 *
 * This was "duplicate doc area #2" (brief §4 `…211608.png`): a list of
 * per-docCode rows with their OWN Upload/Replace buttons, competing with the
 * CIP scan panel and the NRA tabs. It now renders the canonical
 * `DocumentTileGrid` — the SAME grid every other capture surface uses — so
 * there is one tile→modal capture pattern across all entity types.
 *
 * The `/v3/config/document-requirements` catalog (Contract C5) still drives the
 * tile set (one tile per required/optional doc for the entity type). Capturing
 * on a tile runs the canonical CaptureModal (chooser · drag-drop · preflight ·
 * PDF-on-save) and then uploads the produced PDF to ao_Documents via the v3
 * documents route — preserving the original upload/registration callbacks.
 *
 * External props are UNCHANGED so the six entity-dialog mounts + Individual
 * ReviewBody keep compiling.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  DocumentRequirement,
  RequirementLevel,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { RaoPerson } from '../../../types';
import type { AttachedDoc } from './attachedDocs';
import { DocumentTileGrid, type DocTile, type CaptureResult } from '../capture/DocumentTileGrid';
import type { ScanResultPayload } from '../capture/CaptureModal';

// -----------------------------------------------------------------------------
// Public props (UNCHANGED — all existing mounts depend on this shape)
// -----------------------------------------------------------------------------

export interface DocumentSlotsSectionProps {
  person: RaoPerson;
  cartId: string;
  officerCode: string;
  entityType: string;
  /** Fired when an upload completes. `fileUrl` is the stored SharePoint URL. */
  onDocumentUploaded?: (docCode: string, fileUrl: string) => void;
  readOnly?: boolean;
  /**
   * Dialog-level attached documents (from CIP scan or previously-registered
   * uploads). A row in this array fulfills the slot whose docCode matches —
   * seeds the tile's captured state so Edit-mode hydration shows it filled.
   */
  attachedDocs?: ReadonlyArray<AttachedDoc>;
  /**
   * Called when officer captures/uploads a file via this section. The parent
   * lifts the attachment into the shared dialog store so it appears alongside
   * CIP-scan captures and carries through to other dialog surfaces.
   */
  onDocumentRegistered?: (doc: AttachedDoc) => void;
}

// -----------------------------------------------------------------------------
// Fallback requirement when the catalog isn't seeded yet
// -----------------------------------------------------------------------------

function fallbackRequirementsForEntity(entityType: string): ReadonlyArray<DocumentRequirement> {
  if (entityType === 'Individual' || entityType === 'Minor') {
    return [
      {
        requirementId: -1,
        entityType,
        docCode: 'GOVT_ID',
        docLabel: 'Government-issued ID',
        requirementLevel: 'Hard',
        sortOrder: 0,
        isActive: true,
      },
    ];
  }
  return [];
}

// A few well-known docCodes get a real icon; everything else falls back to the
// tile's letter glyph (cleaner than a mismatched icon).
const DOC_CODE_ICON: Record<string, string> = {
  GOVT_ID: 'drivers-license',
  GOVT_ID_GUARDIAN: 'drivers-license',
  PASSPORT: 'foreign-passport',
  SSN_CARD: 'ssn-card',
  GREEN_CARD: 'green-card',
  ADDR_PROOF: 'address-proof',
};

function levelText(level: RequirementLevel, softDueOffsetDays?: number | null): string {
  if (level === 'Hard') return 'Required';
  if (level === 'Soft') {
    return typeof softDueOffsetDays === 'number' ? `Follow-up · due in ${softDueOffsetDays} days` : 'Follow-up';
  }
  return 'Recommended';
}

// -----------------------------------------------------------------------------
// DocumentSlotsSection
// -----------------------------------------------------------------------------

export function DocumentSlotsSection({
  person,
  cartId,
  officerCode,
  entityType,
  onDocumentUploaded,
  readOnly = false,
  attachedDocs,
  onDocumentRegistered,
}: DocumentSlotsSectionProps): JSX.Element {
  const client = useApiClient();

  const [requirements, setRequirements] = useState<ReadonlyArray<DocumentRequirement>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  // Fetch the DocumentRequirements catalog (Contract C5) -----------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCatalogError(null);
    (async () => {
      try {
        const resp = await client.retail.config.documentRequirements.list(entityType);
        if (cancelled) return;
        if (resp.success && resp.data) {
          const active = resp.data.requirements
            .filter((r) => r.isActive)
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder);
          setRequirements(active);
        } else {
          setRequirements(fallbackRequirementsForEntity(entityType));
        }
      } catch (err) {
        if (cancelled) return;
        setRequirements(fallbackRequirementsForEntity(entityType));
        setCatalogError(err instanceof Error ? err.message : 'Failed to load document requirements');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client, entityType]);

  // B2 (Bill 2026-06-03): lazy-fetch a preview for an already-captured doc whose
  // inline image was stripped on persist (existing-CIF reopen — only spItemId
  // survives, per attachedDocs.toPersistedAttachedDoc). Fetches the stored bytes
  // from ao_Documents via the v3 content route, builds a data URL, and feeds it
  // to the tile (→ DocumentTileGrid → QuickPreviewDrawer). Each doc fetched at
  // most once; any failure falls back silently to the tile icon.
  const [previews, setPreviews] = useState<Record<string, { url: string; mime: string }>>({});
  const fetchedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    const pending = (attachedDocs ?? []).filter(
      (d) => d.spItemId && !d.imageDataUrl && !fetchedRef.current.has(d.docCode),
    );
    if (pending.length === 0) return;
    (async () => {
      for (const d of pending) {
        if (cancelled) return;
        fetchedRef.current.add(d.docCode);
        try {
          const resp = await client.retail.documents.content(d.spItemId as string);
          if (cancelled) return;
          if (resp && resp.bytesBase64) {
            const mime = resp.contentType || 'application/pdf';
            setPreviews((prev) => ({
              ...prev,
              [d.docCode]: { url: `data:${mime};base64,${resp.bytesBase64}`, mime },
            }));
          }
        } catch {
          /* icon fallback — preview is best-effort */
        }
      }
    })();
    return () => { cancelled = true; };
  }, [attachedDocs, client]);

  // Build the tile set from requirements, seeding captured state from the
  // dialog-level attachedDocs (CIP scan / prior uploads) so Edit-mode shows
  // already-fulfilled slots as Captured.
  const tiles: ReadonlyArray<DocTile> = useMemo(() => {
    const byCode = new Map<string, AttachedDoc>();
    for (const d of attachedDocs ?? []) byCode.set(d.docCode, d);
    return requirements.map((req) => {
      const fulfilled = byCode.get(req.docCode);
      // B2 — preview source: in-session inline image, else a lazy-fetched SP copy.
      const preview = previews[req.docCode];
      const thumbUrl = fulfilled?.imageDataUrl || preview?.url;
      const thumbMime = fulfilled?.imageDataUrl ? fulfilled.contentType : preview?.mime;
      const tile: DocTile = {
        tileId: req.docCode,
        label: req.docLabel,
        description: req.notes
          ? `${levelText(req.requirementLevel, req.softDueOffsetDays ?? null)} — ${req.notes}`
          : levelText(req.requirementLevel, req.softDueOffsetDays ?? null),
        iconSlug: DOC_CODE_ICON[req.docCode] ?? '',
        required: req.requirementLevel === 'Hard',
        ...(fulfilled ? { capturedFilename: fulfilled.filename } : {}),
        ...(thumbUrl ? { thumbnailDataUrl: thumbUrl } : {}),
        ...(thumbMime ? { thumbnailMimeType: thumbMime } : {}),
      };
      return tile;
    });
  }, [requirements, attachedDocs, previews]);

  // Requirement docs are upload-and-attach (no Azure DI auto-fill), so the
  // extraction dispatch is a successful empty-fields no-op — the CaptureModal
  // still runs its chooser/preflight/PDF-save + Apply confirm.
  const handleScan = useCallback(
    (): Promise<ScanResultPayload> => Promise.resolve({ success: true, fields: {}, needsConfirm: [] }),
    [],
  );

  // On Apply, upload the produced PDF to ao_Documents and register it. Mirrors
  // the old uploadFile path: gated on a resolved CIF (the v3 route 400s
  // without one); pre-CIF captures register locally and re-upload at commit.
  const handleCapture = useCallback(
    (docCode: string, result: CaptureResult): void => {
      const { artifact } = result;
      const filename = artifact.originalFilename
        ? artifact.originalFilename.replace(/\.[^.]+$/, '.pdf')
        : `${docCode}-${Date.now()}.pdf`;

      const register = (fileUrl: string): void => {
        onDocumentUploaded?.(docCode, fileUrl);
        onDocumentRegistered?.({
          docCode,
          source: 'upload',
          filename,
          contentType: 'application/pdf',
          sizeBytes: result.attachedDoc.sizeBytes,
          imageDataUrl: '',
          fileUrl,
          capturedAt: new Date().toISOString(),
        });
      };

      void (async () => {
        if (!person.cifId) {
          // No CIF yet — register locally; cart-commit re-uploads at CIF land.
          register('');
          return;
        }
        try {
          const resp = await client.retail.documents.upload({
            cifId: person.cifId,
            cartId: cartId || undefined,
            fileName: filename,
            contentType: 'application/pdf',
            bytesBase64: artifact.pdfBase64,
            category: 'fsr_upload',
            docCode,
            notes: officerCode ? `Uploaded by ${officerCode}` : undefined,
          });
          register(resp && resp.success && typeof resp.webUrl === 'string' ? resp.webUrl : '');
        } catch {
          register('');
        }
      })();
    },
    [client, cartId, officerCode, person.cifId, onDocumentUploaded, onDocumentRegistered],
  );

  // Render -------------------------------------------------------------------
  if (loading) {
    return (
      <div>
        <div className="pdw-docslots__header">Document Requirements</div>
        <div className="pdw-docslots__muted">Document Requirements loading…</div>
      </div>
    );
  }

  if (requirements.length === 0) {
    return (
      <div>
        <div className="pdw-docslots__header">Document Requirements</div>
        <div className="pdw-docslots__muted">
          No document requirements configured for <strong>{entityType}</strong>.
          {catalogError && <span className="pdw-docslots__err"> ({catalogError})</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="pdw-docslots">
      <div className="pdw-docslots__header">Document Requirements</div>

      {!person.cifId && !readOnly && (
        <div className="pdw-docslots__nocif">
          <strong>No CIF resolved yet.</strong> Captures stage client-side and
          upload to ao_Documents automatically once this person has a Core
          Director CIF (assigned via CustAdd at commit, or via CustInq lookup).
        </div>
      )}

      <DocumentTileGrid
        tiles={tiles}
        cohort={entityType}
        mode="edit"
        readOnly={readOnly}
        onScan={handleScan}
        onCapture={handleCapture}
      />
    </div>
  );
}
